import { DIMENSION_META, type RubricDimensions } from '../../process/creative-process.types'
import type { ScoreRubric } from '../../process/score-explainers'
import { useScoreExplainer } from './score-explainer-context'

interface DimensionMeta {
  key: string
  label: string
  weight?: number
}

interface DimensionBarsProps {
  dimensions: RubricDimensions | Record<string, number>
  /** Which scorecard to render. Defaults to the static 11-dimension rubric (1-5). */
  meta?: ReadonlyArray<DimensionMeta>
  /** Top of the scale (segment count). Static = 5, carousel = 10. */
  max?: number
  /** Which rubric these dimensions belong to, for resolving the right explainer copy. */
  rubric?: ScoreRubric
}

/** Weighted rubric dimensions as compact bars. Static (11 dims, 1-5) by default; a
 *  carousel passes its own meta + max (10 dims, 1-10). Each label opens its explainer. */
export function DimensionBars({
  dimensions,
  meta = DIMENSION_META,
  max = 5,
  rubric = 'static',
}: DimensionBarsProps) {
  const values = dimensions as Record<string, number>
  const { openExplainer } = useScoreExplainer()
  return (
    <div className="flex flex-col gap-1.5">
      {meta.map(({ key, label, weight }) => {
        const value = clamp(values[key], max)
        // A dimension that already scores well has nothing to "fix" — suppress the
        // modal's fix CTA above 70% of the scale.
        const showAction = value / max < 0.7
        return (
          <div key={key} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openExplainer(`${rubric}.${key}`, { showAction })}
              aria-label={`What "${label}" means`}
              className="w-[88px] flex-shrink-0 cursor-help truncate text-left text-[11px] text-paper-dim underline decoration-[color:var(--paper-faint)] decoration-dotted underline-offset-2 transition-colors hover:text-paper"
            >
              {label}
            </button>
            <div className="flex flex-1 gap-0.5" aria-label={`${label}: ${value} of ${max}`}>
              {Array.from({ length: max }, (_, i) => (
                <span
                  key={i}
                  className="h-1.5 flex-1 rounded-full"
                  style={{
                    backgroundColor: i < value ? 'var(--acid)' : 'var(--bg-3)',
                  }}
                />
              ))}
            </div>
            {weight !== undefined && weight >= 2 && (
              <span className="w-4 flex-shrink-0 text-right text-[10px] text-paper-faint">
                ×{weight}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

function clamp(n: number | undefined, max: number): number {
  if (typeof n !== 'number' || !Number.isFinite(n)) return 0
  return Math.max(0, Math.min(max, Math.round(n)))
}
